import React, { useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { FaServer, FaKey, FaLink, FaCheckCircle, FaTruckLoading } from 'react-icons/fa';

const ManageLogistics: React.FC = () => {
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        partnerName: 'SHIPROCKET',
        baseUrl: '',
        apiToken: '',
        extraAuth: '',
        isActive: true
    });

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // ✅ Production aur Localhost APIs
            const response = await fetch('https://www.ayushvatika.com/api/delivery/save-config', {
            // const response = await fetch('http://localhost:8484/api/delivery/save-config', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success(`${formData.partnerName} settings updated!`);
            } else {
                toast.error("Failed to update settings.");
            }
        } catch (error) {
            toast.error("Server error.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout>
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    <header className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <FaTruckLoading className="text-emerald-500" /> Delivery Settings
                        </h1>
                        <p className="text-gray-500 mt-2">Manage API keys and configurations for multiple delivery partners.</p>
                    </header>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div className="bg-emerald-600 p-4 text-white font-bold flex items-center gap-2">
                            <FaServer /> API Configuration Form
                        </div>
                        
                        <form onSubmit={handleSave} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Select Partner</label>
                                    <select 
                                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                        value={formData.partnerName}
                                        onChange={(e) => setFormData({...formData, partnerName: e.target.value})}
                                    >
                                        <option value="SHIPROCKET">Shiprocket</option>
                                        <option value="EKART">Ekart</option>
                                        <option value="DELHIVERY">Delhivery (Cloud Update)</option>
                                        <option value="BLUEDART">Bluedart</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Partner Status</label>
                                    <div className="flex items-center gap-4 py-2">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="sr-only peer" 
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <FaLink className="text-gray-400" /> API Base URL
                                </label>
                                <input 
                                    type="text"
                                    placeholder="https://apiv2.shiprocket.in/v1/external"
                                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                    value={formData.baseUrl}
                                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <FaKey className="text-gray-400" /> API Token / Password
                                </label>
                                <textarea 
                                    rows={3}
                                    placeholder="Paste your password or API token here..."
                                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-mono text-sm"
                                    value={formData.apiToken}
                                    onChange={(e) => setFormData({...formData, apiToken: e.target.value})}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">Extra Credentials (Email/Client ID)</label>
                                <input 
                                    type="text"
                                    placeholder="Email for Shiprocket or Client ID for Ekart"
                                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                    value={formData.extraAuth}
                                    onChange={(e) => setFormData({...formData, extraAuth: e.target.value})}
                                />
                            </div>

                            <div className="pt-4">
                                <button 
                                    type="submit"
                                    disabled={loading}
                                    className="w-full md:w-auto px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <span>Saving...</span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <FaCheckCircle /> Save Configuration
                                        </span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ManageLogistics;